import { useVersion } from '@/contexts/VersionContext';
import { Button } from './ui/button';
import { X, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEffect, useCallback } from 'react';

export function VersionNotification() {
  const { versionNotification, dismissNotification } = useVersion();
  const notification = versionNotification;

  const handleUpdate = useCallback(() => {
    try {
      if (notification?.version) {
        localStorage.setItem('appVersion', notification.version);
      }
    } catch (error) {
      console.warn('Failed to persist appVersion', error);
    }
    console.log('[VersionNotification] Dispatching dashboardChanged (can trigger DashboardContext hard refresh → reload on error)', { version: notification?.version });
    window.dispatchEvent(new Event('dashboardChanged'));

    dismissNotification();
  }, [notification?.version, dismissNotification]);

  const handleDismiss = () => {
    dismissNotification();
  };

  // Auto-run update for force updates without showing UI
  useEffect(() => {
    if (notification?.isForceUpdate) {
      handleUpdate();
    }
  }, [notification?.isForceUpdate, handleUpdate]);

  if (!notification) return null;

  const { isForceUpdate, version, description } = notification;

  // Don't render UI for force updates
  if (isForceUpdate) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-[9999]',
        'bg-white dark:bg-[#0C3451]',
        'border-t border-gray-200 dark:border-gray-600',
        'shadow-lg',
        'animate-in slide-in-from-bottom-2 duration-300'
      )}
    >
      <div className="mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>

            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-gray-900 dark:text-white">
                New Version Available
              </span>
              <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 px-2 py-0.5 rounded-full">
                v{version}
              </span>
            </div>

            {description && (
              <span className="text-xs text-gray-600 dark:text-gray-300 hidden sm:inline">
                {description}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleUpdate}
              className="whitespace-nowrap py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-[#00FEC1] text-black hover:bg-[#00FEC1]/80 rounded-sm px-2 flex items-center justify-center h-7 font-normal transition-all duration-200"
            >
              Update
            </Button>
            <button
              onClick={handleDismiss}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-3 h-3 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
